import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { adminStopViewAs } from "@/lib/journeyApi";

/**
 * Sticky red banner shown across every page whenever an admin is
 * "viewing as" another user. Stays out of the layout (position
 * sticky) so it overlays the sidebar's top edge and is impossible
 * to miss. Clicking Stop restores the admin's original session and
 * sends them straight back to /admin so they can continue triaging.
 */
export default function ImpersonationBanner() {
  const { user, checkAuth } = useAuth();
  const [stopping, setStopping] = useState(false);
  const navigate = useNavigate();

  const handleStop = useCallback(async () => {
    setStopping(true);
    try {
      await adminStopViewAs();
      await checkAuth();
      toast.success("Stopped viewing-as. Back to your admin account.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't end view-as");
      setStopping(false);
    }
  }, [checkAuth, navigate]);

  if (!user?.impersonator_email) return null;

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 w-full bg-red-600/95 text-white border-b border-red-400 shadow-lg backdrop-blur-sm"
    >
      <div className="px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap font-mono text-xs">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          <span className="uppercase tracking-[0.25em] font-bold">View-as mode</span>
        </span>
        <span className="opacity-90">
          You ({user.impersonator_email}) are viewing the app as
        </span>
        <strong className="font-bold" data-testid="impersonation-target-email">
          {user.email}
        </strong>
        <span className="opacity-80">— writes are blocked.</span>
        <button
          type="button"
          onClick={handleStop}
          disabled={stopping}
          data-testid="impersonation-stop-btn"
          className="ml-auto px-3 py-1 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50 rounded-sm font-bold uppercase tracking-wider transition-colors"
        >
          {stopping ? "Stopping…" : "✕ Stop"}
        </button>
      </div>
    </div>
  );
}
